import { useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import { PlanType } from '@/services/billing/plans';

/**
 * The contracts-sales `current_plan` label (distinct from the grid's `getCurrentPlan`): trials report
 * 'trial', active legacy plans report 'legacy', otherwise the raw plan type.
 */
function currentContractsPlan(isTrial: boolean, isSubscriptionActive: boolean, planType: PlanType | undefined): string {
  if (isTrial) {
    return 'trial';
  }
  if (isSubscriptionActive && (planType === PlanType.NotReportable || planType === PlanType.Basic)) {
    return 'legacy';
  }
  return planType ?? '';
}

type UseContractsSalesAnalyticsArgs = {
  isOpen: boolean;
  planClicked: PlanType | undefined;
};

/**
 * Analytics for the contracts-sales sidebar. Auto-fires the open/close events via an effect, and
 * returns the imperative trackers the field/cancel/submit handlers call.
 */
export function useContractsSalesAnalytics({ isOpen, planClicked }: UseContractsSalesAnalyticsArgs) {
  const { account, user } = useAuth();
  const { track } = useAnalytics();
  const isTrial = useStore(billing.isTrial);
  const isSubscriptionActive = useStore(billing.isSubscriptionActive);
  const planType = useStore(billing.planType);

  const base = {
    'Account Id': account.id,
    'Requester Name': `${user.first_name} ${user.last_name}`.trim(),
    'Requester Email': user.email,
    'Requester User Id': user.id,
    'Current Plan': currentContractsPlan(isTrial, isSubscriptionActive, planType),
    'Requested Plan': planClicked,
  };

  const trackers = {
    trackFieldInput: (field: string) =>
      void track('inline_contracts_sales', 'formFieldInput', { ...base, 'Field Name': field }),
    trackCancelClicked: () => void track('inline_contracts_sales', 'formCancelClicked', base),
    trackCancelDismissed: () => void track('inline_contracts_sales', 'formCancelDismissed', base),
    trackSubmitClicked: () => void track('inline_contracts_sales', 'formSubmitClicked', base),
    trackSubmitError: () => void track('inline_contracts_sales', 'formSubmitError', base),
    trackSubmitSuccess: () => void track('inline_contracts_sales', 'formSubmitSuccess', base),
  };

  // Fire "opened"/"closed" on the open↔close transition (the drawer stays mounted across opens).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      wasOpen.current = true;
      void track('inline_contracts_sales', 'formOpened', base);
    } else if (!isOpen && wasOpen.current) {
      wasOpen.current = false;
      void track('inline_contracts_sales', 'formClosed', base);
    }
    // `base`/`track` are recreated each render; the ref guard keeps this to one fire per transition.
  }, [isOpen, base, track]);

  return trackers;
}
